import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  defaultLinearGradient,
  type GradientStop,
  type LinearGradientModel,
  MIN_GRADIENT_STOPS,
  normalizeGradientAngle,
  withStopInWidestGap,
} from 'shared/fabric';
import { theme } from 'styles/theme/theme';

/** Цвет, от которого включается градиент, если у фигуры нет сплошной заливки-строки. */
const DEFAULT_COLOR = theme.colors.DarkBlue200;

interface Aggregate<T> {
  value: T | undefined;
  isMixed: boolean;
}

export interface FillGradientControl {
  enabled: Aggregate<boolean>;
  angle: Aggregate<number>;
  /** Общий список точек выделения; `isMixed` — у объектов он разный, и точки не редактируются. */
  stops: Aggregate<GradientStop[]>;
  setEnabled: (on: boolean) => void;
  /** Угол каждому объекту с градиентом — его собственные точки остаются. */
  setAngle: (angle: number) => void;
  updateStop: (index: number, patch: Partial<GradientStop>) => void;
  addStop: () => void;
  removeStop: (index: number) => void;
}

function aggregate<T>(
  objects: fabric.Object[],
  read: (o: fabric.Object) => T | undefined,
  same: (a: T | undefined, b: T | undefined) => boolean = (a, b) => a === b,
): Aggregate<T> {
  if (objects.length === 0) return { value: undefined, isMixed: false };
  const first = read(objects[0]);
  for (let i = 1; i < objects.length; i++) {
    if (!same(read(objects[i]), first)) return { value: undefined, isMixed: true };
  }
  return { value: first, isMixed: false };
}

const sameStops = (a: GradientStop[] | undefined, b: GradientStop[] | undefined): boolean => JSON.stringify(a) === JSON.stringify(b);

/**
 * Linear gradient of one or more selected objects as angle + stops — the model the
 * broadcast board editor uses (`shared/fabric`). Reads `editor.fillGradient.get`,
 * i.e. `fill` itself, so a template saved by the old two-stop section shows its 3
 * stops. Every write is one `set` per object: one `object:modified`, one undo
 * step; the hook re-reads on `object:modified`, so undo keeps the section in sync.
 * Selecting an object writes nothing — an untouched gradient is never rebuilt.
 */
export function useFillGradient(objects: fabric.Object[], editor: FabricEditor): FillGradientControl {
  const objectSet = useMemo(() => new Set(objects), [objects]);

  const modelOf = useCallback((o: fabric.Object): LinearGradientModel | undefined => editor.fillGradient?.get(o), [editor]);

  const read = useCallback(
    () => ({
      enabled: aggregate(objects, (o) => Boolean(modelOf(o))),
      angle: aggregate(objects, (o) => modelOf(o)?.angle),
      stops: aggregate(objects, (o) => modelOf(o)?.stops, sameStops),
    }),
    [objects, modelOf],
  );

  const [state, setState] = useState(read);

  useEffect(() => {
    setState(read());
    const off = editor.on('object:modified', (e) => {
      if (objectSet.has(e.target as fabric.Object)) setState(read());
    });
    return () => off();
  }, [editor, objectSet, read]);

  /** Applies `change` to every selected object that has a readable gradient; `undefined` skips the object. */
  const updateModels = useCallback(
    (change: (model: LinearGradientModel) => LinearGradientModel | undefined) => {
      for (const o of objects) {
        const model = modelOf(o);
        const next = model && change(model);
        if (next) editor.fillGradient?.set(o, next);
      }
      setState(read());
    },
    [objects, editor, modelOf, read],
  );

  const setEnabled = useCallback(
    (on: boolean) => {
      for (const o of objects) {
        if (on === Boolean(modelOf(o))) continue; // already in target state
        const base = typeof o.fill === 'string' && o.fill ? o.fill : DEFAULT_COLOR;
        editor.fillGradient?.set(o, on ? defaultLinearGradient(base) : undefined);
      }
      setState(read());
    },
    [objects, editor, modelOf, read],
  );

  const setAngle = useCallback(
    (value: number) => {
      const angle = normalizeGradientAngle(value);
      updateModels((model) => (model.angle === angle ? undefined : { ...model, angle }));
    },
    [updateModels],
  );

  const updateStop = useCallback(
    (index: number, patch: Partial<GradientStop>) =>
      updateModels((model) => {
        if (!model.stops[index]) return undefined;
        return { ...model, stops: model.stops.map((stop, i) => (i === index ? { ...stop, ...patch } : stop)) };
      }),
    [updateModels],
  );

  const addStop = useCallback(() => updateModels((model) => ({ ...model, stops: withStopInWidestGap(model.stops) })), [updateModels]);

  const removeStop = useCallback(
    (index: number) =>
      updateModels((model) =>
        model.stops.length <= MIN_GRADIENT_STOPS ? undefined : { ...model, stops: model.stops.filter((_, i) => i !== index) },
      ),
    [updateModels],
  );

  return { ...state, setEnabled, setAngle, updateStop, addStop, removeStop };
}
